import {
  GridColumns,
  DataGrid,
  GridRowsProp,
  GridRowIdGetter,
  GridFeatureMode,
} from '@mui/x-data-grid';
import { translate } from '@portal/services/i18n';
import React, { useMemo } from 'react';

interface IProps {
  columns: GridColumns;
  rows: GridRowsProp;
  page?: number;
  pageSize: number;
  rowCount?: number;
  orderBy: string;
  sort: 'asc' | 'desc';
  rowsPerPageOptions?: number[];
  getRowId?: GridRowIdGetter;
  onChange?: (params: advancedFilterModels.baseFilter) => void;
  paginationMode?: GridFeatureMode;
  lineText?: string;
  pagination?: true | undefined;
}

const DataTable: React.FC<IProps> = ({
  columns,
  rows,
  page,
  pageSize,
  rowCount,
  orderBy,
  sort,
  rowsPerPageOptions,
  getRowId,
  onChange,
  paginationMode = 'server',
  pagination = undefined,
  lineText,
}: IProps) => {
  const onPage = (page: number) => {
    if (pageSize === 100) {
      return;
    }

    const baseFilter: advancedFilterModels.baseFilter = {
      orderBy: orderBy,
      page,
      pageSize: pageSize,
      sort: sort,
      limit: pageSize,
    };

    onChange?.(baseFilter);
  };

  const CustomNoRowsOverlay = () => {
    return (
      <div className="datatable__no-rows-overlay">
        {lineText ?? translate('COMPONENTS.DATA_TABLE.NO_RESULT')}
      </div>
    );
  };

  const columnsMemo = useMemo((): GridColumns => {
    return columns.map((column) => ({
      ...column,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'datatable__header',
      description: column.headerName,
      cellClassName: 'datatable__cell',
    }));
  }, [columns]);

  return (
    <div className="datatable">
      <DataGrid
        autoHeight
        sx={{
          border: 1,
          borderColor: '#E0E0E0',
          '& .MuiDataGrid-columnSeparator': {
            visibility: 'hidden',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#ffffff',
            color: '@primary',
          },
        }}
        getRowId={getRowId}
        paginationMode={paginationMode}
        rows={rows}
        columns={columnsMemo}
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}
        onPageChange={(value) => {
          onPage(value);
        }}
        components={{
          NoResultsOverlay: CustomNoRowsOverlay,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        disableColumnFilter
        disableColumnSelector
        disableSelectionOnClick
        disableColumnMenu
        rowsPerPageOptions={rowsPerPageOptions ?? []}
        pagination={pagination}
      />
    </div>
  );
};

export default DataTable;
